import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Search, MapPin, Tag, Calendar, ExternalLink, ChevronDown, Wrench } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EVENT_TYPES = ['Expo', 'Demo', 'Campout', 'Live Show', 'Festival', 'Service', 'Meet-Up', 'Workshop', 'AEONrv Meet-Up', 'AEONrv Service'];

const TYPE_COLORS = {
  'Expo': { bg: '#FFF0E8', text: '#E8520A', border: '#E8520A' },
  'Demo': { bg: '#EEF2FF', text: '#4F46E5', border: '#4F46E5' },
  'Campout': { bg: '#ECFDF5', text: '#059669', border: '#059669' },
  'Live Show': { bg: '#FEF3C7', text: '#D97706', border: '#D97706' },
  'Festival': { bg: '#FDF2F8', text: '#DB2777', border: '#DB2777' },
  'Service': { bg: '#F0F9FF', text: '#0284C7', border: '#0284C7' },
  'Meet-Up': { bg: '#F5F3FF', text: '#7C3AED', border: '#7C3AED' },
  'Workshop': { bg: '#FEF9C3', text: '#A16207', border: '#A16207' },
  'AEONrv Meet-Up': { bg: '#F5F3FF', text: '#7C3AED', border: '#7C3AED' },
  'AEONrv Service': { bg: '#F0F9FF', text: '#0284C7', border: '#0284C7' },
};

const getTypeColor = (type) => TYPE_COLORS[type] || { bg: '#f3f4f6', text: '#6b7280', border: '#6b7280' };

// City coordinates lookup
const CITY_COORDS = {
  'austin, texas': [30.267, -97.743],
  'burlington, north carolina': [36.096, -79.437],
  'alpharetta, georgia': [34.075, -84.294],
  'jackson, mississippi': [32.299, -90.185],
  'albuquerque, new mexico': [35.085, -106.651],
  'flagstaff, arizona': [35.198, -111.651],
  'prescott, arizona': [34.540, -112.469],
  'phoenix, arizona': [33.449, -112.074],
  'san diego, california': [32.716, -117.161],
  'palm springs, california': [33.830, -116.545],
  'anaheim, california': [33.836, -117.914],
  'san juan capistrano, california': [33.502, -117.662],
  'hood river, oregon': [45.706, -121.521],
  'redmond, oregon': [44.273, -121.174],
  'palisades north lake tahoe, california': [39.197, -120.235],
  'oak ridge estate, virginia': [37.726, -78.933],
  'arrington, virginia': [37.726, -78.933],
  'chilliwack, british columbia': [49.168, -121.951],
  'coombs, british columbia': [49.302, -124.401],
};

const getCoords = (event) => {
  const key = `${event.city}, ${event.state}`.toLowerCase();
  return CITY_COORDS[key] || null;
};

// Custom pin icon - color coded by event type
const makeIcon = (color) => new L.DivIcon({
  className: '',
  html: `<div style="width:28px;height:28px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg></div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

const FitBounds = ({ positions }) => {
  const map = useMap();
  useEffect(() => {
    if (positions.length > 0) {
      const bounds = L.latLngBounds(positions);
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 6 });
    }
  }, [positions, map]);
  return null;
};

const EventCard = ({ event, isPast, fr }) => {
  const start = new Date(event.start_date + 'T00:00:00');
  const end = event.end_date ? new Date(event.end_date + 'T00:00:00') : null;
  const dateStr = end && event.start_date !== event.end_date
    ? `${start.toLocaleDateString(fr ? 'fr-CA' : 'en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString(fr ? 'fr-CA' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    : start.toLocaleDateString(fr ? 'fr-CA' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const isAeonrvLocal = event.event_type === 'AEONrv Meet-Up' || event.event_type === 'AEONrv Service';
  const hasBannerImage = !isAeonrvLocal && event.image;

  return (
    <div
      className={`group relative border rounded-xl overflow-hidden flex flex-col transition-all duration-300 ${
        isPast
          ? 'border-gray-200 grayscale opacity-70 hover:grayscale-0 hover:opacity-100 hover:border-orange-300 hover:shadow-lg'
          : 'border-gray-200 hover:border-orange-300 hover:shadow-lg'
      }`}
      data-testid={`event-card-${event.id}`}
    >
      {/* Partner logo badge — top right */}
      {event.partner === 'AEONrv' && (
        <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5">
          {event.event_type === 'AEONrv Service' && (
            <div className="bg-orange-500 rounded-lg p-1.5 shadow-sm">
              <Wrench size={12} className="text-white" />
            </div>
          )}
          <div className="bg-white/90 backdrop-blur-sm rounded-lg px-2 py-1 shadow-sm">
            <img src="/aeonrv_logo.svg" alt="AEONrv" className="h-4 w-auto" />
          </div>
        </div>
      )}

      {/* Date banner — always visible */}
      <div className="h-28 flex items-center justify-center flex-shrink-0" style={{ background: isPast ? '#f3f4f6' : 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)' }}>
        <div className="text-center">
          <p className={`text-2xl font-black ${isPast ? 'text-gray-400' : 'text-white'}`}>
            {start.toLocaleDateString(fr ? 'fr-CA' : 'en-US', { month: 'short' }).toUpperCase()}
          </p>
          <p className={`text-3xl font-black leading-none ${isPast ? 'text-gray-500' : 'text-white'}`}>
            {start.getDate()}{end && event.start_date !== event.end_date ? `-${end.getDate()}` : ''}
          </p>
        </div>
      </div>

      {/* Expanding banner on hover — between date and content */}
      <div 
        className="grid transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:grid-rows-[1fr] grid-rows-[0fr]"
      >
        <div className="overflow-hidden">
          {isAeonrvLocal ? (
            <div className="h-24 flex items-center justify-center bg-white border-b border-gray-100">
              <img src="/aeonrv_logo.svg" alt="AEONrv" className="h-8 w-auto" />
            </div>
          ) : hasBannerImage ? (
            <div>
              <img src={event.image} alt={event.title} className="w-full h-auto" />
            </div>
          ) : (
            <div className="h-24 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #E8520A 0%, #ff7b3a 100%)' }}>
              <p className="text-white font-black text-lg uppercase tracking-widest">{event.event_type}</p>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-5 flex-1 flex flex-col">
        {/* Type badge + Status */}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ backgroundColor: isPast ? '#f3f4f6' : getTypeColor(event.event_type).bg, color: isPast ? '#9ca3af' : getTypeColor(event.event_type).text }}>
            {event.event_type}
          </span>
          {isPast && (
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
              {fr ? 'Terminé' : 'Past'}
            </span>
          )}
        </div>

        {/* Title */}
        <h3 className="text-sm font-black text-gray-900 mb-2 group-hover:text-orange-600 transition-colors leading-tight">
          {event.title}
        </h3>

        {/* Date */}
        <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1.5">
          <Calendar size={11} className="flex-shrink-0" />
          {dateStr}
        </div>

        {/* Location */}
        <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-3">
          <MapPin size={11} className="flex-shrink-0" />
          {event.city}{event.state ? `, ${event.state}` : ''}{event.country ? `, ${event.country}` : ''}
        </div>

        {/* Partner */}
        {event.partner && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-3">
            <Tag size={11} className="flex-shrink-0" />
            <span className="font-semibold text-gray-700">{event.partner}</span>
          </div>
        )}

        {/* Description */}
        {event.description && (
          <p className="text-xs text-gray-400 leading-relaxed mb-4 line-clamp-3">{event.description}</p>
        )}

        {/* CTA */}
        <div className="mt-auto pt-2">
          {event.url ? (
            <a
              href={event.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 w-full justify-center px-4 py-2.5 text-xs font-bold uppercase tracking-widest rounded-lg transition-all duration-200"
              style={{
                backgroundColor: isPast ? '#f3f4f6' : '#E8520A',
                color: isPast ? '#6b7280' : '#fff',
              }}
              onMouseEnter={e => { if (isPast) { e.currentTarget.style.backgroundColor = '#E8520A'; e.currentTarget.style.color = '#fff'; } }}
              onMouseLeave={e => { if (isPast) { e.currentTarget.style.backgroundColor = '#f3f4f6'; e.currentTarget.style.color = '#6b7280'; } }}
              data-testid={`event-cta-${event.id}`}
            >
              {fr ? 'PLUS D\'INFO' : 'MORE INFO'} <ExternalLink size={11} />
            </a>
          ) : (
            <span className="inline-flex items-center gap-1.5 w-full justify-center px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-gray-400 bg-gray-100 rounded-lg">
              {fr ? 'Détails à venir' : 'Details coming soon'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const Events = () => {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageLive, setPageLive] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const mapRef = useRef(null);

  const scrollToMap = () => {
    mapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    // Check if page is live
    fetch(`${BACKEND_URL}/api/events/page-status`)
      .then(r => r.json())
      .then(data => setPageLive(data.live))
      .catch(() => setPageLive(false));

    fetch(`${BACKEND_URL}/api/events`)
      .then(r => r.json())
      .then(data => { setEvents(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const now = new Date().toISOString().split('T')[0];

  // Dynamic filter options from events data
  const countries = useMemo(() => [...new Set(events.map(e => e.country).filter(Boolean))].sort(), [events]);
  const states = useMemo(() => {
    const filtered = countryFilter ? events.filter(e => e.country === countryFilter) : events;
    return [...new Set(filtered.map(e => e.state).filter(Boolean))].sort();
  }, [events, countryFilter]);
  const types = useMemo(() => [...new Set(events.map(e => e.event_type).filter(Boolean))].sort(), [events]);

  // Filter events
  const filtered = useMemo(() => {
    return events.filter(e => {
      if (search) {
        const q = search.toLowerCase();
        const match = (e.title || '').toLowerCase().includes(q) ||
          (e.partner || '').toLowerCase().includes(q) ||
          (e.city || '').toLowerCase().includes(q) ||
          (e.venue || '').toLowerCase().includes(q) ||
          (e.description || '').toLowerCase().includes(q);
        if (!match) return false;
      }
      if (typeFilter && e.event_type !== typeFilter) return false;
      if (countryFilter && e.country !== countryFilter) return false;
      if (stateFilter && e.state !== stateFilter) return false;
      return true;
    });
  }, [events, search, typeFilter, countryFilter, stateFilter]);

  // Group by country
  const groupedByCountry = useMemo(() => {
    const groups = {};
    filtered.forEach(e => {
      const key = e.country || 'Other';
      if (!groups[key]) groups[key] = [];
      groups[key].push(e);
    });
    // Sort: upcoming first (by date asc), then past (by date desc)
    Object.values(groups).forEach(arr => arr.sort((a, b) => {
      const aIsPast = (a.end_date || a.start_date) < now;
      const bIsPast = (b.end_date || b.start_date) < now;
      if (aIsPast !== bIsPast) return aIsPast ? 1 : -1; // upcoming first
      return a.start_date.localeCompare(b.start_date); // within group: by date
    }));
    return Object.entries(groups).sort(([a], [b]) => {
      // Canada first, then alphabetical
      if (a === 'Canada') return -1;
      if (b === 'Canada') return 1;
      return a.localeCompare(b);
    });
  }, [filtered, now]);

  const upcomingCount = filtered.filter(e => (e.end_date || e.start_date) >= now).length;
  const pastCount = filtered.filter(e => (e.end_date || e.start_date) < now).length;

  // If page is off, show coming soon
  if (pageLive === false) {
    return (
      <div className="min-h-screen pt-[76px] flex items-center justify-center" style={{ backgroundColor: '#000' }}>
        <div className="text-center px-4">
          <h1 className="text-3xl lg:text-4xl font-black text-white mb-4 uppercase">
            {fr ? 'Page en construction' : 'Coming Soon'}
          </h1>
          <p className="text-gray-400 text-sm max-w-md mx-auto">
            {fr ? 'Notre page événements sera bientôt disponible.' : 'Our events page will be available soon.'}
          </p>
        </div>
      </div>
    );
  }

  if (pageLive === null) return null;

  return (
    <div className="min-h-screen pt-[76px]" style={{ backgroundColor: '#000' }}>
      <SEO
        title={fr ? "Événements" : "Events"}
        description="Find upcoming expos, demos and live experiences where you can see Total Composites and our partners in person."
        path="/events"
      />

      {/* Hero */}
      <section className="bg-black py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#E8520A', letterSpacing: '0.15em' }}>
            {fr ? 'Événements et salons' : 'Events & Shows'}
          </p>
          <h1 className="text-4xl lg:text-5xl font-black text-white mb-4 uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>
            {fr ? 'TROUVEZ VOTRE PROCHAIN ÉVÉNEMENT' : 'FIND YOUR NEXT EVENT'}
          </h1>
          <p className="text-gray-400 max-w-2xl leading-relaxed text-sm">
            {fr
              ? "Découvrez les salons, expos et événements où Total Composites et nos partenaires seront présents. Venez voir nos produits en personne."
              : "Discover the expos, demos and live experiences where Total Composites and our partners will be. Come see our products in person."}
          </p>
        </div>
      </section>

      {/* Search Bar */}
      <section className="bg-gray-50 border-b border-gray-200 py-6 sticky top-[76px] z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-stretch gap-3">
            {/* Name search */}
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={fr ? "Rechercher un événement..." : "Search events..."}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm text-gray-900 bg-white focus:outline-none focus:border-orange-300 transition-colors"
                data-testid="events-search"
              />
            </div>

            {/* Category */}
            <div className="relative">
              <Tag size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <select
                value={typeFilter}
                onChange={e => setTypeFilter(e.target.value)}
                className="appearance-none pl-9 pr-8 py-3 border border-gray-200 rounded-xl text-sm text-gray-900 bg-white focus:outline-none focus:border-orange-300 cursor-pointer"
                data-testid="events-type-filter"
              >
                <option value="">{fr ? 'Toutes catégories' : 'All Categories'}</option>
                {types.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <ChevronDown size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>

            {/* Country */}
            <div className="relative">
              <MapPin size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <select
                value={countryFilter}
                onChange={e => { setCountryFilter(e.target.value); setStateFilter(''); }}
                className="appearance-none pl-9 pr-8 py-3 border border-gray-200 rounded-xl text-sm text-gray-900 bg-white focus:outline-none focus:border-orange-300 cursor-pointer"
                data-testid="events-country-filter"
              >
                <option value="">{fr ? 'Tous les pays' : 'All Countries'}</option>
                {countries.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <ChevronDown size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>

            {/* State — only show when states exist */}
            {states.length > 0 && (
              <div className="relative">
                <MapPin size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <select
                  value={stateFilter}
                  onChange={e => setStateFilter(e.target.value)}
                  className="appearance-none pl-9 pr-8 py-3 border border-gray-200 rounded-xl text-sm text-gray-900 bg-white focus:outline-none focus:border-orange-300 cursor-pointer"
                  data-testid="events-state-filter"
                >
                  <option value="">{fr ? 'Tous les états' : 'All States'}</option>
                  {states.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <ChevronDown size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            )}
          </div>

          {/* Result count + Map CTA */}
          <div className="flex items-center justify-between mt-3">
            <p className="text-xs text-gray-400">
              {filtered.length} {fr ? (filtered.length === 1 ? 'événement' : 'événements') : (filtered.length === 1 ? 'event' : 'events')} — {upcomingCount} {fr ? 'à venir' : 'upcoming'}, {pastCount} {fr ? (pastCount === 1 ? 'passé' : 'passés') : 'past'}
            </p>
            <button
              onClick={scrollToMap}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold transition-all duration-200 hover:shadow-md"
              style={{ backgroundColor: '#1a1a1a', color: '#fff' }}
              data-testid="scroll-to-map-btn"
            >
              <MapPin size={13} />
              {fr ? 'Voir la carte' : 'View Map'}
            </button>
          </div>
        </div>
      </section>

      {/* Events by Country */}
      <section className="py-16 bg-white" data-testid="events-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => <div key={`skel-${i}`} className="bg-gray-100 rounded-xl h-72 animate-pulse" />)}
            </div>
          ) : groupedByCountry.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-400 text-sm">{fr ? 'Aucun événement trouvé.' : 'No events found.'}</p>
            </div>
          ) : (
            groupedByCountry.map(([country, countryEvents]) => (
              <div key={country} className="mb-16 last:mb-0">
                {/* Country header — same as Partners */}
                <div className="flex items-center gap-4 mb-8">
                  <h2 className="text-2xl font-black text-gray-900 uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {country}
                  </h2>
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-xs font-bold text-gray-400">{countryEvents.length} {fr ? (countryEvents.length === 1 ? 'événement' : 'événements') : (countryEvents.length === 1 ? 'event' : 'events')}</span>
                </div>

                {/* Card grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {countryEvents.map(event => (
                    <EventCard
                      key={event.id}
                      event={event}
                      isPast={(event.end_date || event.start_date) < now}
                      fr={fr}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </section>
      {/* Interactive Map — upcoming only */}
      {!loading && filtered.length > 0 && (() => {
        const pins = filtered
          .filter(e => (e.end_date || e.start_date) >= now)
          .map(e => ({ ...e, coords: getCoords(e) }))
          .filter(e => e.coords);
        if (pins.length === 0) return null;
        const positions = pins.map(p => p.coords);
        // Collect unique types for legend
        const pinTypes = [...new Set(pins.map(p => p.event_type))];
        return (
          <section ref={mapRef} className="py-16 bg-gray-50 border-t border-gray-200" data-testid="events-map-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-4 mb-8">
                <h2 className="text-2xl font-black text-gray-900 uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {fr ? 'Carte des événements' : 'Event Locations'}
                </h2>
                <div className="flex-1 h-px bg-gray-200" />
                <div className="flex items-center gap-3 flex-wrap text-xs text-gray-500">
                  {pinTypes.map(t => (
                    <span key={t} className="flex items-center gap-1.5">
                      <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: getTypeColor(t).border }} />
                      {t}
                    </span>
                  ))}
                </div>
              </div>
              <div className="rounded-xl overflow-hidden shadow-lg border border-gray-200" style={{ height: '480px' }}>
                <MapContainer
                  center={[39, -98]}
                  zoom={4}
                  style={{ height: '100%', width: '100%' }}
                  scrollWheelZoom={false}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                  />
                  <FitBounds positions={positions} />
                  {pins.map(pin => (
                    <Marker key={pin.id} position={pin.coords} icon={makeIcon(getTypeColor(pin.event_type).border)}>
                      <Popup>
                        <div style={{ minWidth: 180 }}>
                          {pin.partner === 'AEONrv' && <img src="/aeonrv_logo.svg" alt="AEONrv" style={{ height: 16, marginBottom: 6 }} />}
                          <p style={{ fontWeight: 800, fontSize: 13, margin: '0 0 4px', color: '#111' }}>{pin.title}</p>
                          <p style={{ fontSize: 11, color: '#666', margin: '0 0 2px' }}>{pin.start_date}{pin.end_date !== pin.start_date ? ` → ${pin.end_date}` : ''}</p>
                          <p style={{ fontSize: 11, color: '#666', margin: '0 0 6px' }}>{pin.city}{pin.state ? `, ${pin.state}` : ''}</p>
                          <span style={{ fontSize: 10, fontWeight: 700, backgroundColor: getTypeColor(pin.event_type).bg, color: getTypeColor(pin.event_type).text, padding: '2px 6px', borderRadius: 4 }}>{pin.event_type}</span>
                          {pin.url && (
                            <a href={pin.url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginTop: 8, fontSize: 11, fontWeight: 700, color: '#E8520A', textDecoration: 'none' }}>
                              MORE INFO →
                            </a>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
            </div>
          </section>
        );
      })()}
    </div>
  );
};

export default Events;
